import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import { useDebounce } from '@/hooks/use-debounce';
import { http } from '@/lib/http';
import { Loader2, Check } from 'lucide-react';
import { useWorkspaceStore } from '@/stores/workspace';
import { SearchResult } from '@/stores/search';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const Highlight = ({ text, highlight }: { text: string; highlight: string }) => {
    if (!highlight.trim() || !text) {
        return <>{text}</>;
    }
    const escapedHighlight = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedHighlight})`, 'gi');
    const parts = text.split(regex);

    return (
        <>
            {parts.map((part, i) =>
                i % 2 === 1 ? (
                    <mark key={i} className="bg-primary/20 p-0 m-0 rounded-sm">
                        {part}
                    </mark>
                ) : (
                    part
                )
            )}
        </>
    );
};

export const WorkspaceSearch = () => {
    const [query, setQuery] = useState('');
    const debouncedQuery = useDebounce(query, 300);
    const [results, setResults] = useState<Record<string, SearchResult[]>>({});
    const [openAccordions, setOpenAccordions] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const { openInPreview, userSelectedFiles, setUserSelectedFiles } = useWorkspaceStore(state => ({
        openInPreview: state.openInPreview,
        userSelectedFiles: state.userSelectedFiles,
        setUserSelectedFiles: state.setUserSelectedFiles,
    }));
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        // Automatically focus the input when the component mounts
        inputRef.current?.focus();
    }, []);

    const handleFileToggle = (path: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const isSelected = userSelectedFiles.has(path);
        setUserSelectedFiles(prev => {
            const next = new Set(prev);
            if (isSelected) {
                next.delete(path);
            } else {
                next.add(path);
            }
            return next;
        });
        toast.info(`${path} ${isSelected ? 'removed from' : 'added to'} context.`);
    };

    const performSearch = useCallback(async () => {
        if (!debouncedQuery.trim()) {
            setResults({});
            setOpenAccordions([]);
            return;
        }
        setLoading(true);
        try {
            const params = new URLSearchParams({ q: debouncedQuery, type: 'code' });
            const res = await http<{ results: SearchResult[] }>(`/api/search?${params.toString()}`);
            
            const groupedResults: Record<string, SearchResult[]> = {};
            for (const result of res.results) {
                if (result.path) {
                    if (!groupedResults[result.path]) {
                        groupedResults[result.path] = [];
                    }
                    groupedResults[result.path].push(result);
                }
            }
            setResults(groupedResults);
            setOpenAccordions(Object.keys(groupedResults));
        } catch (error) {
            console.error("Workspace search failed", error);
            setResults({});
            setOpenAccordions([]);
        } finally {
            setLoading(false);
        }
    }, [debouncedQuery]);

    useEffect(() => {
        performSearch();
    }, [performSearch]);

    const handleResultClick = (event: React.MouseEvent, path: string, line?: number) => {
        if (event.shiftKey) {
            handleFileToggle(path, event);
        } else {
            openInPreview(path, line ? { line } : undefined);
        }
    };

    return (
        <div className="flex flex-col h-full gap-2 px-1">
            <div className="relative">
                <Input
                    ref={inputRef}
                    id="workspace-search-input"
                    placeholder="Search file contents..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="pr-8"
                />
                {loading && <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
            <ScrollArea className="flex-1">
                {Object.keys(results).length > 0 ? (
                    <Accordion type="multiple" className="w-full" value={openAccordions} onValueChange={setOpenAccordions}>
                        {Object.entries(results).map(([path, matches]) => (
                            <AccordionItem value={path} key={path}>
                                <AccordionTrigger className="text-xs py-2 hover:no-underline">
                                    <div className="truncate flex items-center gap-2 w-full">
                                        <button
                                            onClick={(e) => handleFileToggle(path, e)}
                                            className={`h-4 w-4 flex-shrink-0 rounded border transition-colors flex items-center justify-center ${
                                            userSelectedFiles.has(path)
                                                ? "bg-primary text-primary-foreground border-primary"
                                                : "border-muted-foreground/30 hover:border-muted-foreground"
                                            }`}
                                        >
                                            {userSelectedFiles.has(path) && <Check className="h-3 w-3" />}
                                        </button>
                                        <span className="font-semibold">{path}</span>
                                        <Badge variant="secondary">{matches.length}</Badge>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent className="pb-1">
                                    <ul className="pl-4">
                                        {matches.map((match, index) => (
                                            <li key={index} className="text-xs" >
                                                <Button variant="ghost" className="w-full justify-start h-auto py-1 px-2" onClick={(e) => handleResultClick(e, match.path!, match.line)}>
                                                    <div className="flex items-start gap-2 w-full">
                                                        <span className="w-10 text-right text-muted-foreground pt-0.5">{match.line}:</span>
                                                        <code className="text-left font-mono whitespace-pre-wrap break-all"><Highlight text={match.snippet?.trim() || ''} highlight={query} /></code>
                                                    </div>
                                                </Button>
                                            </li>
                                        ))}
                                    </ul>
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                ) : (
                    !loading && debouncedQuery && <p className="text-center text-xs text-muted-foreground p-4">No results found for "{debouncedQuery}".</p>
                )}
            </ScrollArea>
        </div>
    );
};